import { AxiosInstance } from 'axios'
import { useMutation, useQueryClient } from 'react-query'

import { useAxiosContext } from '@dc-shared-web/providers/AxiosProvider'

import { useToast } from '@dc-shared-web/components/shadcn/use-toast'
import {
  LIST_QUERY_KEY,
  PROJECT_MEMBERS_QUERY_KEY,
} from '@dc-shared-web/hooks/api/queryKeys'

type RevokeInvitationParams = {
  id: string
  email: string
}

type RevokeInvitationResponse = {
  status: 'error' | 'success'
  message?: string
  error?: string
}

const revokeInvitation = async (
  apiClient: AxiosInstance,
  params: RevokeInvitationParams
): Promise<RevokeInvitationResponse> => {
  const { data } = await apiClient.postForm(
    `/ajax/invite/revoke/${params.id}`,
    {
      id: params.id,
    }
  )

  return data.data
}

export const useRevokeInvitation = (onSuccess?: () => void) => {
  const apiClient = useAxiosContext()
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation(
    (mutationParams: RevokeInvitationParams) =>
      revokeInvitation(apiClient, mutationParams),
    {
      onSuccess: (_, successParams) => {
        queryClient.invalidateQueries({
          // When an invitation is revoked a new record is created. We don't
          // know where that record will be created in accordance with the
          // current sort selection, so we need to invalidate all queries
          queryKey: ['invitations'],
        })
        queryClient.invalidateQueries([
          PROJECT_MEMBERS_QUERY_KEY,
          LIST_QUERY_KEY,
        ])
        toast({
          variant: 'success',
          title: 'Invitation revoked',
          description: `The invitation sent to ${successParams.email} has been revoked.`,
        })
        onSuccess?.()
      },
      onError: () => {
        toast({
          variant: 'error',
          title: 'Error revoking invitation',
          description: 'If the issue persists, please contact support.',
        })
      },
    }
  )
}
